import type { ComponentProps } from "react";
import type {
  NumberFormatValues,
  NumericFormatProps,
} from "react-number-format";
import { NumericFormat } from "react-number-format";

import { cn } from "@/lib/utils";

import { Input } from "../ui/input";

export function HeadingLabelCell({
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <LabelCell
      {...props}
      className={cn(
        "text-body-sm text-text-label md:text-body-sm lg:text-body px-2 py-4 uppercase md:max-w-none md:px-4",
        className
      )}
    />
  );
}

export function LabelCell({
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      {...props}
      className={cn(
        "text-body-sm text-text-label lg:text-body sticky left-0 w-[180px] overflow-x-auto px-2 py-4 md:max-w-none md:px-4",
        className
      )}
    />
  );
}

export function NumberDisplayCell({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  if (typeof children !== "number")
    return (
      <td
        {...props}
        className={cn(
          "text-body-sm text-text-label md:text-body w-16 max-w-16 overflow-x-auto px-2 py-4 text-end md:px-4",
          className
        )}
      >
        {children}
      </td>
    );
  return (
    <td
      {...props}
      className={cn(
        "text-body-sm text-text-label md:text-body w-16 max-w-16 overflow-x-auto p-2 text-end md:px-4",
        className
      )}
    >
      {formatNumber(children)}
    </td>
  );
}

export function PercentageDisplayCell({
  className,
  children,
  ...props
}: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "children"> & {
  children: number;
}) {
  return (
    <td
      {...props}
      className={cn(
        "text-text-label w-16 max-w-16 overflow-x-auto p-2 text-end md:px-4",
        className
      )}
    >
      {floorToTwoDecimals(children)}%
    </td>
  );
}

export function Tr({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      {...props}
      className={cn(
        "border-border-strong/10 border-b p-2 md:border-x md:px-4",
        className
      )}
    />
  );
}

export type CurrencyInputProps = Omit<
  ComponentProps<typeof Input>,
  "onChange" | "value" | "type"
> &
  Omit<
    NumericFormatProps,
    | "onChange"
    | "decimalScale"
    | "thousandSeparator"
    | "customInput"
    | "allowNegative"
    | "handleValueChange"
  > & {
    inverse?: boolean;
    onChange?: (value: number) => void;
  };

export function CustomInput({
  className,
  ...props
}: React.HTMLAttributes<HTMLInputElement>) {
  return (
    <Input
      {...props}
      className={cn(
        "bg-surface text-body-sm lg:text-body h-10 w-full [appearance:textfield] rounded text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        className
      )}
    />
  );
}

export function CurrencyInput({
  value,
  onChange,
  defaultValue,
  ...props
}: CurrencyInputProps) {
  const handleValueChange = ({ floatValue }: NumberFormatValues) => {
    onChange?.(floatValue ?? 0);
  };

  return (
    <NumericFormat
      {...props}
      prefix={props.prefix || ""}
      defaultValue={defaultValue}
      value={value}
      customInput={CustomInput}
      allowNegative={false}
      thousandSeparator=","
      decimalScale={2}
      onValueChange={handleValueChange}
    />
  );
}

export function sum(values: number[]) {
  return values.reduce((acc, v) => acc + v, 0);
}

export function floorToTwoDecimals(value: number) {
  return Math.floor(value * 100) / 100;
}

export function formatNumber(value: number) {
  return value.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
}

export function Table({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <table {...props} className={cn("min-w-full table-fixed", className)} />
  );
}

export function TableHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "border-pulse-100 flex justify-between rounded-lg border bg-blue-800 px-4 py-5 md:rounded-t-lg md:rounded-b-none md:border-0 md:border-x md:border-t",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
